/**
 * Locale preference sync bridge.
 *
 * `useLanguage().setLanguage()` lives in the app/i18n layer and must not depend
 * on auth/identity. This module decouples the two: the auth layer registers a
 * handler that knows the current provider/user identity and the right
 * preference endpoint to call, and `setLanguage` fires the handler when the
 * user explicitly changes language.
 *
 * Mirrors the `setRefreshTokenCallback` bridge in `shared/lib/axios.ts`.
 *
 * Sync is best-effort and fire-and-forget: if no handler is registered (e.g.
 * unauthenticated, first render) or the network call fails, language switching
 * still works locally via i18next + localStorage. This keeps the installed PWA
 * fully functional offline.
 */

export type LocaleSyncHandler = (locale: string) => void;

let handler: LocaleSyncHandler | null = null;

/** Register (or clear, with `null`) the active locale-sync handler. */
export const registerLocaleSyncHandler = (
  next: LocaleSyncHandler | null,
): void => {
  handler = next;
};

/** Fire the registered handler, swallowing any error (best-effort). */
export const syncPreferredLocale = (locale: string): void => {
  if (!handler) return;
  try {
    handler(locale);
  } catch {
    // Never let a preference-sync failure break a language change.
  }
};
